import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, Validator } from '@angular/forms';
import { ValueState } from '@app/shared/types';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface SingleValueTextOutput {
  id: string;
  value: string;
  mandatory?: boolean;
  dataType?: { strictMin?: number; strictMax?: number };
  visible?: boolean;
  isValid: boolean;
  error: string | undefined;
}

@Component({
  selector: 'app-single-value-text',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './single-value-text.component.html',
  styleUrls: ['./single-value-text.component.css'],
})
export class SingleValueTextComponent
  implements OnChanges, Validator, OnDestroy, AfterViewInit
{
  @Input() public data!: {
    state: ValueState;
    label: string;
    mandatory?: boolean;
    dataType?: { strictMin?: number; strictMax?: number };
    visible?: boolean;
    isValid?: boolean;
    error?: string;
  };

  @Output() output = new EventEmitter<SingleValueTextOutput>();

  inputValue: string = '';
  errorMessage: string | null = null;
  isValid = true;

  private inputChanged$ = new Subject<string>();
  private inputSub!: Subscription;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.data) {
      this.inputValue =
        this.data.state.value === '~NULL~' ? '' : String(this.data.state.value);
      this.validateInput();
    }

    if (!this.inputSub) {
      this.inputSub = this.inputChanged$
        .pipe(debounceTime(800))
        .subscribe(() => {
          this.validateInput();
          this.emitOutput();
        });
    }
  }

  ngAfterViewInit() {
    this.inputChanged$.next(this.inputValue);
  }

  onDataChange(event: any) {
    this.inputValue = event.target.value;
    this.inputChanged$.next(this.inputValue);
  }

  private emitOutput() {
    this.output.emit({
      id: this.data.state.id,
      value: this.inputValue,
      mandatory: this.data.mandatory,
      dataType: this.data.dataType,
      visible: this.data.visible,
      isValid: this.isValid,
      error: this.errorMessage || undefined,
    });
  }

  private validateInput() {
    const val = this.inputValue?.trim() ?? '';

    this.isValid = false;

    if (!this.data.mandatory && val === '') {
      this.errorMessage = null;
      this.isValid = true;
      return;
    }

    if (this.data.mandatory && val === '') {
      this.errorMessage = 'Polje je obavezno.';
      return;
    }

    if (
      this.data.state.id === 'patient_id' ||
      (this.data.state.id === 'P_JMBG' && val.length > 0)
    ) {
      const jmbgRegex = /^[0-9]{13}$/;
      if (!jmbgRegex.test(val)) {
        this.errorMessage = 'Polje mora sadržati tačno 13 cifara.';
        return;
      }
    }

    if (
      this.data.dataType?.strictMin &&
      val.length < this.data.dataType.strictMin
    ) {
      this.errorMessage = `Minimum length is ${this.data.dataType.strictMin}.`;
      return;
    }

    if (
      this.data.dataType?.strictMax &&
      val.length > this.data.dataType.strictMax
    ) {
      this.errorMessage = `Maximum length is ${this.data.dataType.strictMax}.`;
      return;
    }

    this.errorMessage = null;
    this.isValid = true;
  }

  validate() {
    if (this.errorMessage) {
      return { invalid: this.errorMessage };
    }
    return null;
  }

  ngOnDestroy() {
    this.inputSub?.unsubscribe();
  }
}
